import DetailIcon from "@mui/icons-material/RemoveRedEye";
import { CUSTOM_FIELD_TYPE } from "fieldpro-tools/dist/src/types";
import _ from "lodash";

import {
  EditIcon,
  TMenuButtonOption,
} from "components/Buttons/MenuButton/MenuButton";
import BasicLockIcon from "components/Dialog/BasicDialog/Icons/BasicLockIcon";
import { DownloadIcon } from "components/Input/InputFile/InputFile";
import { TABLE_COLUMN_TYPE, TColumnType } from "components/Table/model";
import CustomTooltip from "components/Tooltip/CustomTooltip";
import TLang from "model/application/Lang";
import { ITableAction } from "model/application/Table";
import { IList, IListSchema } from "model/entities/List";
import { combineDefaultAndStoredColumns } from "utils/utils";

const getOptionsOfChoiceFilter = (
  schema: IListSchema[],
  nameFilter: string,
): { name: string; options: any[] } | undefined => {
  const schemaColumn = schema.find((s) => s.column_tag === nameFilter);
  if (!schemaColumn?.options) {
    return undefined;
  }

  return {
    name: `${nameFilter}Options`,
    options: schemaColumn.options.map((opt) => ({ ...opt, id: opt.key })),
  };
};

export const getAllFiltersOptionsForChoiceInputs = (
  schema: IListSchema[],
): { name: string; options: any[] }[] => {
  const filtersOptions: { name: string; options: any[] }[] = [];
  schema
    .filter(
      (att) =>
        att.type === CUSTOM_FIELD_TYPE.SINGLE_CHOICE ||
        att.type === CUSTOM_FIELD_TYPE.MULTIPLE_CHOICE,
    )
    .forEach((att) => {
      const filterOption = getOptionsOfChoiceFilter(schema, att.column_tag);
      if (filterOption) {
        filtersOptions.push(filterOption);
      }
    });

  return filtersOptions;
};

export const getColumnTypeFromAttrType = (
  attrType: CUSTOM_FIELD_TYPE,
): TABLE_COLUMN_TYPE => {
  switch (attrType) {
    case CUSTOM_FIELD_TYPE.UPLOAD_FILE:
      return TABLE_COLUMN_TYPE.UPLOAD;
    case CUSTOM_FIELD_TYPE.BOOLEAN:
      return TABLE_COLUMN_TYPE.SWITCH;
    case CUSTOM_FIELD_TYPE.DATE_PICKER:
      return TABLE_COLUMN_TYPE.DATE;
    case CUSTOM_FIELD_TYPE.GPS:
      return TABLE_COLUMN_TYPE.GPS;
    case CUSTOM_FIELD_TYPE.PICTURE:
    case CUSTOM_FIELD_TYPE.MULTI_PICTURE:
      return TABLE_COLUMN_TYPE.PICTURE;
    case CUSTOM_FIELD_TYPE.SIGNATURE:
      return TABLE_COLUMN_TYPE.PICTURE;
    case CUSTOM_FIELD_TYPE.SINGLE_CHOICE:
      return TABLE_COLUMN_TYPE.SINGLE_CHOICE;
    case CUSTOM_FIELD_TYPE.SINGLE_CHOICE_ON_LIST:
      return TABLE_COLUMN_TYPE.SINGLE_CHOICE_ON_LIST;
    case CUSTOM_FIELD_TYPE.MULTIPLE_CHOICE:
      return TABLE_COLUMN_TYPE.MULTIPLE_CHOICE;
    case CUSTOM_FIELD_TYPE.MULTIPLE_CHOICE_ON_LIST:
      return TABLE_COLUMN_TYPE.MULTIPLE_CHOICE_ON_LIST;
    case CUSTOM_FIELD_TYPE.INTEGER:
    case CUSTOM_FIELD_TYPE.DECIMAL:
    case CUSTOM_FIELD_TYPE.COMPUTE:
      return TABLE_COLUMN_TYPE.NUMBER;
    case CUSTOM_FIELD_TYPE.MULTIPLE_INPUT_INTEGER:
      return TABLE_COLUMN_TYPE.MULTIPLE_INPUT_INTEGER;
    case CUSTOM_FIELD_TYPE.MULTIPLE_INPUT_DECIMAL:
      return TABLE_COLUMN_TYPE.MULTIPLE_INPUT_DECIMAL;
    case CUSTOM_FIELD_TYPE.TEXT:
    case CUSTOM_FIELD_TYPE.BARCODE_SCANNER:
    case CUSTOM_FIELD_TYPE.PHONE_NUMBER:
      return TABLE_COLUMN_TYPE.TEXT;
    case CUSTOM_FIELD_TYPE.MATRIX_ON_LIST:
      return TABLE_COLUMN_TYPE.MATRIX_ON_LIST;
    case CUSTOM_FIELD_TYPE.GEO_DELIMITATION:
      return TABLE_COLUMN_TYPE.GEO_DELIMITATION;
    case CUSTOM_FIELD_TYPE.JSON:
      return TABLE_COLUMN_TYPE.JSON;
  }
  return TABLE_COLUMN_TYPE.TEXT;
};

const addUnsortableToColumnTypes = (
  columns: TColumnType[],
  schema: IListSchema[],
) => {
  return columns.map((column) => {
    return {
      ...column,
      unsortable: !getBackendSortableColumns(schema).includes(column.name),
    };
  });
};

export const getColumnTypes = (
  list: IList,
  lang: TLang,
  isFieldProConnectEnabled: boolean = false,
  storedColumnTypes?: TColumnType[],
  isValidationEnabled: boolean = false,
): TColumnType[] => {
  let result: TColumnType[] = [
    {
      name: "_displayed_name",
      type: TABLE_COLUMN_TYPE.TEXT,
      label:
        lang.containers.workflows.subCategories.activityReports.columns
          .itemName,
      hidden: false,
    },
  ];
  if (list.business_id_template) {
    result.push({
      name: "_displayed_business_id",
      type: TABLE_COLUMN_TYPE.TEXT,
      label:
        lang.containers.workflows.subCategories.activityReports.columns
          .businessId,
      hidden: false,
    });
  }
  if (list.show_ids) {
    result = [
      {
        name: "item_id",
        type: TABLE_COLUMN_TYPE.TEXT,
        label: lang.containers.lists.subCategories.items.columns.itemId,
        hidden: false,
      },
      ...result,
    ];
  }

  // Don't show deprecated attributes at all;
  const activeSchema = getActiveSchema(list?.schema);

  const schemaColumns = _.map(activeSchema, (att) => ({
    name: att.column_tag,
    type: getColumnTypeFromAttrType(att.type),
    label: att.column_name,
    hidden: !isImportant(att, list?.important), // Only show Important attributes by default
  }));

  result = _.concat(result, schemaColumns);

  if (
    ["TEAM", "SINGLE_TEAM", "MOBILE_USER", "SINGLE_MOBILE_USER"].includes(
      list.scope,
    )
  )
    result.push({
      name: "_owners",
      type: TABLE_COLUMN_TYPE.MULTIPLE_CHOICE,
      label: lang.components.filters.owners,
      hidden: false,
    });

  const itemLang = lang.containers.lists.subCategories.items.columns;

  if (isValidationEnabled) {
    result.push({
      name: "_validation_status",
      label: itemLang.validationStatus,
      type: TABLE_COLUMN_TYPE.CUSTOM,
      hidden: false,
    });

    result.push({
      name: "_validation_updated_at",
      label: itemLang.validationUpdatedAt,
      type: TABLE_COLUMN_TYPE.DATE,
      hidden: true,
    });

    result.push({
      name: "_validation_message",
      label: itemLang.validationMessage,
      type: TABLE_COLUMN_TYPE.TEXT,
      hidden: true,
    });
  }

  if (list.list_type === "CUSTOMER") {
    result.push({
      name: "_last_visit_date",
      type: TABLE_COLUMN_TYPE.DATE,
      label: lang.components.maps.legend.lastVisit,
      hidden: false,
    });
    result.push({
      name: "_last_activity_date",
      type: TABLE_COLUMN_TYPE.DATE,
      label:
        lang.containers.lists.subCategories.lists.createEditModal.customersItem
          .options.lastActivityDate,
      hidden: false,
    });

    if (isFieldProConnectEnabled && isCustomerListWithTaxId(list)) {
      result.push({
        name: "_loyalty_balance",
        type: TABLE_COLUMN_TYPE.NUMBER,
        label: lang.containers.lists.subCategories.items.columns.loyaltyBalance,
        hidden: false,
      });
    }

    result.push({
      name: "_created_at",
      type: TABLE_COLUMN_TYPE.DATE,
      label:
        lang.containers.workflows.subCategories.activityReports.columns
          .createdAt,
      hidden: false,
    });
  }

  result = addUnsortableToColumnTypes(result, list?.schema);
  return combineDefaultAndStoredColumns(result, storedColumnTypes);
};

export const getActiveSchema = (schema: IListSchema[]) => {
  return _.filter(schema, (att) => !att.deprecated);
};

export const isCustomerListWithTaxId = ({
  id,
  schema,
}: {
  id: string;
  schema?: IListSchema[];
}): boolean => {
  const isCustomerList = id === "customer";
  const hasTaxIdColumn =
    schema?.some((schema) => schema.column_tag === "taxid") ?? false;
  return isCustomerList && hasTaxIdColumn;
};

export const isImportant = (attribute: IListSchema, important?: string[]) => {
  return _.includes(important, attribute.column_tag);
};

export const getBackendSortableColumns = (schema: IListSchema[]) => {
  const nonCustomFields = [
    "_active",
    "_name",
    "_displayed_name",
    "_displayed_business_id",
    "_created_at",
    "_created_locally_at",
    "_created_source",
    "_created_by",
    "_updated_at",
    "_updated_locally_at",
    "_updated_source",
    "_updated_by",
    "_last_visit_date",
  ];
  const backendSortableColumns = schema
    .filter((s) =>
      [
        CUSTOM_FIELD_TYPE.BOOLEAN,
        CUSTOM_FIELD_TYPE.COMPUTE,
        CUSTOM_FIELD_TYPE.DATE_PICKER,
        CUSTOM_FIELD_TYPE.DECIMAL,
        CUSTOM_FIELD_TYPE.INTEGER,
        CUSTOM_FIELD_TYPE.TIME_PICKER,
        CUSTOM_FIELD_TYPE.TEXT,
        CUSTOM_FIELD_TYPE.RATING,
        CUSTOM_FIELD_TYPE.PHONE_NUMBER,
        CUSTOM_FIELD_TYPE.SINGLE_CHOICE,
      ].includes(s.type),
    )
    .map((s) => s.column_tag)
    .concat(...nonCustomFields);
  return backendSortableColumns;
};

export const getAvailableMenuActions = (
  list: IList,
  lang: TLang,
  downloadAction?: ITableAction,
  editListAction?: ITableAction,
  viewListAction?: ITableAction,
  isOptimetriksAdmin?: boolean,
) => {
  const availableActions: TMenuButtonOption[] = [];
  const listLangLabel = lang.containers.lists.subCategories.lists;
  const isListLocked = list.locked && !isOptimetriksAdmin;

  if (editListAction) {
    availableActions.push({
      onClick: () => editListAction.action(list),
      label: (
        <span style={{ display: "flex" }}>
          {listLangLabel.createEditModal.editSchema.title}
          {isListLocked && (
            <CustomTooltip
              placement="top"
              title={
                listLangLabel.createEditModal.editSchema.hoverToolTips.locked
              }
            >
              <BasicLockIcon style={{ pointerEvents: "auto" }} />
            </CustomTooltip>
          )}
        </span>
      ),
      icon: <EditIcon />,
      disabled: isListLocked ? true : false,
    });
  }

  if (viewListAction) {
    availableActions.push({
      onClick: () => viewListAction.action({ id: list.id }),
      label: listLangLabel.createEditModal.viewSchema.title,
      icon: (
        <DetailIcon
          style={{ marginLeft: "-4px", fontSize: "20px", marginRight: "4px" }}
        />
      ),
    });
  }

  if (downloadAction) {
    availableActions.push({
      onClick: () => downloadAction.action(list.id),
      label: lang.containers.lists.subCategories.items.actions.download,
      icon: (
        <DownloadIcon
          style={{ marginLeft: "-4px", fontSize: "20px", marginRight: "4px" }}
        />
      ),
    });
  }

  const menuLabel =
    availableActions.length === 1
      ? availableActions[0].label
      : listLangLabel.moreActions.title;

  return {
    menuActions: availableActions,
    menuLabel,
  };
};
